import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Text, Flex, HStack, Heading, Icon, VStack, Spinner,
} from '@chakra-ui/react';
import { FiCheckCircle, FiXCircle, FiRotateCw, FiInfo, FiSquare } from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import Page from '../components/ui/Page';
import SectionCard from '../components/ui/SectionCard';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import StatusBadge from '../components/ui/StatusBadge';
import client from '../utils/fetchClient';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const EVENT_ICON = { pass: FiCheckCircle, fail: FiXCircle, retry: FiRotateCw, info: FiInfo };
const EVENT_COLOR = { pass: 'status.pass', fail: 'status.fail', retry: 'status.flakyText', info: 'gray.400' };

const fmtDuration = (ms) => {
  if (!ms || Number.isNaN(Number(ms))) return '-';
  const s = Math.round(Number(ms) / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};
const fmtClock = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/**
 * Subscribes to GET /api/test-runs/:id/stream (Server-Sent Events) instead of
 * polling - the server pushes a frame the moment a test finishes, so updates
 * land in well under a second instead of waiting out a poll interval. The
 * browser's EventSource auto-reconnects on a dropped connection on its own.
 */
function useLiveRun(runId) {
  const [run, setRun] = useState(null);
  const [events, setEvents] = useState([]);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!runId) return undefined;
    setRun(null); setEvents([]); setDone(false); setLoading(true); setError(null);
    let connectedOnce = false;

    const es = new EventSource(`${API_BASE_URL}/api/test-runs/${runId}/stream`, { withCredentials: true });

    es.addEventListener('snapshot', (e) => {
      const data = JSON.parse(e.data);
      connectedOnce = true;
      setRun(data.run);
      setProgress(data.progress);
      setDone(data.done);
      setEvents(data.events || []);
      setLoading(false);
      setError(null);
      if (data.done) es.close(); // server already ended the stream in this case too
    });

    es.addEventListener('event', (e) => {
      const data = JSON.parse(e.data);
      setRun(data.run);
      setProgress(data.progress);
      setDone(data.done);
      setEvents((prev) => [...prev, data.event]);
    });

    es.addEventListener('finished', (e) => {
      const data = JSON.parse(e.data);
      setRun(data.run);
      setProgress(data.progress);
      setDone(true);
      es.close();
    });

    es.onerror = () => {
      if (!connectedOnce) setError('Failed to connect to run');
      setLoading(false);
    };

    return () => es.close();
  }, [runId]);

  return { run, events, progress, done, loading, error };
}

function EventRow({ event }) {
  const color = EVENT_COLOR[event.kind] || 'gray.400';
  return (
    <Flex gap={2.5} align="flex-start" py={1.5}>
      <Icon as={EVENT_ICON[event.kind] || FiInfo} color={color} boxSize="14px" mt="2px" flexShrink={0} />
      <Text fontSize="12.5px" color="gray.700" fontFamily="mono" flex={1} noOfLines={1}>{event.text}</Text>
      <Text fontSize="11px" color="gray.400" fontFamily="mono" whiteSpace="nowrap">{fmtClock(event.ts)}</Text>
    </Flex>
  );
}

export default function LiveRunPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { run, events, progress, done, loading, error } = useLiveRun(runId);
  const [aborting, setAborting] = useState(false);
  const feedRef = useRef(null);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [events.length]);

  const onAbort = async () => {
    if (!window.confirm('Abort this run?')) return;
    setAborting(true);
    try {
      const res = await client.post(`/api/test-runs/${runId}/abort`);
      if (!res.ok) throw new Error(await res.text());
      // No manual refetch needed - the open SSE connection will push the
      // 'finished' frame the abort triggers.
    } catch (e) {
      window.alert(e.message || 'Failed to abort run');
    } finally {
      setAborting(false);
    }
  };

  if (loading) return <LoadingState message="Connecting to run" minH="60vh" />;
  if (error && !run) return <ErrorState message={error} onRetry={() => window.location.reload()} />;
  if (!run) return <ErrorState message="Test run not found." />;

  const ran = (run.passedTests || 0) + (run.failedTests || 0) + (run.skippedTests || 0);

  return (
    <Page maxW="6xl">
      <Breadcrumbs items={[{ label: 'Test runs', to: '/test-runs' }, { label: `Run #${String(run.id).slice(0, 8)}` }]} />

      <SectionCard mb={5}>
        <Flex align="center" gap={3} wrap="wrap">
          {!done && <Spinner size="sm" color="brand.500" thickness="2px" />}
          <Heading size="md" fontWeight="800" letterSpacing="-0.3px" color="gray.900">
            {run.name || 'Test run'} <Text as="span" color="gray.400" fontWeight="600">#{String(run.id).slice(0, 8)}</Text>
          </Heading>
          <StatusBadge status={run.status} />
          <Box flex="1" />
          {!done && can('abortRuns') && (
            <Button size="sm" variant="outline" colorScheme="red" leftIcon={<FiSquare />} onClick={onAbort} isLoading={aborting}>
              Abort
            </Button>
          )}
          {done && (
            <Button size="sm" variant="outline" onClick={() => navigate(`/test-runs/${run.id}`)}>View full run</Button>
          )}
        </Flex>

        <Flex gap={5} wrap="wrap" mt={3.5} fontSize="12.5px" color="gray.500">
          {run.branch && <Text>branch <Text as="span" fontFamily="mono" color="gray.700">{run.branch}</Text></Text>}
          {run.commit && <Text>commit <Text as="span" fontFamily="mono" color="brand.600">{String(run.commit).slice(0, 8)}</Text></Text>}
          {run.environment && <Text>env <Text as="span" color="gray.700">{run.environment}</Text></Text>}
          <Text>started <Text as="span" color="gray.700">{run.startTime ? new Date(run.startTime).toLocaleString() : '-'}</Text></Text>
          {done && <Text>duration <Text as="span" fontFamily="mono" color="gray.700">{fmtDuration(new Date(run.endTime) - new Date(run.startTime))}</Text></Text>}
        </Flex>

        <Box mt={4}>
          <Flex justify="space-between" mb={1.5} fontSize="11.5px" color="gray.400" fontFamily="mono">
            <span>{ran} / {run.totalTests || '?'} tests</span>
            <span>{progress}%</span>
          </Flex>
          <Box h="8px" borderRadius="full" bg="gray.100" overflow="hidden">
            <Box h="100%" w={`${progress}%`} bg={run.failedTests > 0 ? 'status.fail' : 'brand.500'} transition="width 0.3s" />
          </Box>
        </Box>

        <Flex align="center" gap={4} mt={4} wrap="wrap">
          <Text fontSize="12.5px" fontWeight="600" color="status.pass">{run.passedTests || 0} passed</Text>
          <Text fontSize="12.5px" fontWeight="600" color="status.fail">{run.failedTests || 0} failed</Text>
          <Text fontSize="12.5px" fontWeight="600" color="status.skip">{run.skippedTests || 0} skipped</Text>
        </Flex>
      </SectionCard>

      <SectionCard>
        <Text fontSize="13px" fontWeight="700" textTransform="uppercase" letterSpacing="0.05em" color="gray.400" mb={3}>
          Live feed
        </Text>
        <VStack
          ref={feedRef}
          align="stretch"
          spacing={0}
          maxH="480px"
          overflowY="auto"
          divider={<Box borderBottom="1px solid" borderColor="gray.100" />}
        >
          {events.length === 0 ? (
            <HStack color="gray.400" fontSize="13px" py={4}>
              {!done && <Spinner size="xs" />}
              <Text>{done ? 'No events recorded for this run.' : 'Waiting for the first result…'}</Text>
            </HStack>
          ) : (
            events.map((e) => <EventRow key={e.id} event={e} />)
          )}
        </VStack>
      </SectionCard>
    </Page>
  );
}
